import React from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  useTheme,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { Videocam as VideocamIcon, Mic as MicIcon } from "@mui/icons-material";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { useVideoModeAvailability } from "../../../hooks/useVideoModeAvailability";
import { useTooltipOnTap } from "../../../hooks/useTooltipOnTap";
import {
  dialogPaperSx,
  dialogContentSx,
  titleSx,
  buttonsContainerSx,
  goButtonSx,
  audioOnlyButtonSx,
} from "./styles";

interface CoachingModelDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (audioOnly: boolean) => void;
  submittingMode?: "video" | "audio" | null;
}

export const CoachingModelDialog: React.FC<CoachingModelDialogProps> = ({
  open,
  onClose,
  onSelect,
  submittingMode = null,
}) => {
  const theme = useTheme();
  const { isVideoModeAvailable, isCheckingNetwork, videoDisabledReason } =
    useVideoModeAvailability(open);

  const isModeSubmitting = submittingMode !== null;

  const showVideoNetworkHint =
    !isVideoModeAvailable && !isCheckingNetwork && !isModeSubmitting;
  const videoTooltipTitle = showVideoNetworkHint
    ? (videoDisabledReason ?? "")
    : "";
  const {
    tooltipProps: videoTooltipProps,
    handleWrapperClick: handleVideoTooltipClick,
    showTapHint: showVideoTapHint,
  } = useTooltipOnTap(videoTooltipTitle);

  const handleAudioOnly = () => {
    onSelect(true);
  };

  const handleVideo = () => {
    onSelect(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: dialogPaperSx(theme),
      }}
    >
      <DialogContent sx={dialogContentSx}>
        <CustomTypography sx={titleSx(theme)}>Select Coaching</CustomTypography>

        <Box sx={buttonsContainerSx}>
          {showVideoNetworkHint ? (
            <Tooltip title={videoTooltipTitle} arrow {...videoTooltipProps}>
              <Box
                component="span"
                onClick={showVideoTapHint ? handleVideoTooltipClick : undefined}
                sx={{
                  display: "block",
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleVideo}
                  disabled={isModeSubmitting || !isVideoModeAvailable}
                  sx={[goButtonSx, { pointerEvents: "none" }] as SxProps<Theme>}
                  startIcon={<VideocamIcon />}
                >
                  {submittingMode === "video" ? "Please wait..." : "Video Mode"}
                </Button>
              </Box>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={handleVideo}
              disabled={isModeSubmitting || !isVideoModeAvailable}
              sx={goButtonSx}
              startIcon={<VideocamIcon />}
            >
              {submittingMode === "video" ? "Please wait..." : "Video Mode"}
            </Button>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={handleAudioOnly}
            disabled={isModeSubmitting}
            sx={audioOnlyButtonSx}
            startIcon={<MicIcon />}
          >
            {submittingMode === "audio" ? "Please wait..." : "Audio Mode"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
